import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import Swal from "sweetalert2";

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info"); // 'success', 'error', 'info', 'warning'
  const [alertColor, setAlertColor] = useState(null);

  const showToast = useCallback((msg, type = "info", color = null) => {
    setMessage(msg);
    setSeverity(type);
    setAlertColor(color);
    setOpen(true);
  }, []);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  // Helper functions for specific types
  const notify = {
    success: (msg) => showToast(msg, "success"),
    error: (msg) => showToast(msg, "error"),
    info: (msg) => showToast(msg, "info"),
    warning: (msg, options = {}) =>
      showToast(msg, "warning", options.color || null),

    // Confirmation Dialog (Blocking) - Modern SweetAlert2 style
    confirm: async (title, text, confirmButtonText = "Ya, Hapus!") => {
      const result = await Swal.fire({
        title: title || "Apakah anda yakin?",
        text: text || "Data yang dihapus tidak dapat dikembalikan!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: confirmButtonText,
        cancelButtonText: "Batal",
        reverseButtons: true,
        customClass: {
          popup: "swal2-modern-popup", // We can add custom CSS if needed
        },
      });
      return result;
    },
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: "100%",
            fontWeight: "bold",
            ...(alertColor === "red" && {
              bgcolor: "#d32f2f",
              color: "#fff",
              "& .MuiAlert-icon, & .MuiAlert-action": { color: "#fff" },
            }),
          }}
          variant="filled"
          elevation={6}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
